import { defineLiteral } from "../types/define-literal.js";
import { Option } from "effect";
import * as S from "effect/Schema";
import { ConditionSchemaName, TriggerEventName, VitrineTriggerEventName } from "@biteinc/enums";
//#region src/wire/recommendation-condition.ts
/**
* Prebuilt recommendation rule conditions. A rule stores its condition only as JSON Schema, which
* the recommendation engine evaluates against the ask state. Editors offer templates (items in
* cart, pre-checkout popup, …) and recover a template by decoding a stored schema against each
* template's exact shape: extra keys, a reordered `required` list, or an empty timing enum make it
* custom, as in the legacy bureau `RecommendationRuleHelper` this replaces.
*
* `DietaryRestrictionsApplied` has no template: legacy offered it but saved no condition, so
* editors must not offer it.
*/
/** A stored condition: free-form JSON Schema, as the recommendations service accepts it. */
const RecommendationConditionSchemaJson = S.Record(S.String, S.Unknown);
const EDITABLE_CONDITION_SCHEMA_NAMES = Object.values(ConditionSchemaName).filter((name) => name !== ConditionSchemaName.DietaryRestrictionsApplied);
const EXACT_PARSE_OPTIONS = defineLiteral({ onExcessProperty: "error" });
const ObjectType = S.Literal("object");
const StringType = S.Literal("string");
const IdEnum = S.Struct({
	type: StringType,
	enum: S.Array(S.String)
});
const TimingIdEnum = S.Struct({
	type: StringType,
	enum: S.NonEmptyArray(S.String)
});
const ItemIdEnumProperties = S.Struct({ id: IdEnum });
const SectionIdEnumProperties = S.Struct({ sectionId: IdEnum });
function makeInCartSchema(idField, idProperties) {
	return S.Struct({
		type: ObjectType,
		required: S.Array(S.String),
		properties: S.Struct({
			cartItems: S.Struct({
				type: S.Literal("array"),
				minItems: S.Literal(1),
				contains: S.Struct({
					type: ObjectType,
					required: S.Tuple([S.Literal(idField)]),
					properties: idProperties
				})
			}),
			timingId: S.optionalKey(TimingIdEnum)
		})
	});
}
function makeSelectedItemSchema(idField, idProperties) {
	return S.Struct({
		type: ObjectType,
		required: S.Array(S.String),
		properties: S.Struct({
			selectedItem: S.Struct({
				type: ObjectType,
				required: S.Tuple([S.Literal(idField)]),
				properties: idProperties
			}),
			timingId: S.optionalKey(TimingIdEnum)
		})
	});
}
function makeTriggerEventSchema(triggerEventName) {
	return S.Struct({
		type: ObjectType,
		required: S.Array(S.String),
		properties: S.Struct({
			triggerEventName: S.Struct({
				type: StringType,
				const: S.Literal(triggerEventName)
			}),
			timingId: S.optionalKey(TimingIdEnum)
		})
	});
}
const ItemsInCartSchema = makeInCartSchema("id", ItemIdEnumProperties);
const SectionsInCartSchema = makeInCartSchema("sectionId", SectionIdEnumProperties);
const ItemIsSelectedSchema = makeSelectedItemSchema("id", ItemIdEnumProperties);
const SectionIsSelectedSchema = makeSelectedItemSchema("sectionId", SectionIdEnumProperties);
const ItemsSelectedOrInCartSchema = S.Struct({ anyOf: S.Tuple([ItemsInCartSchema, ItemIsSelectedSchema]) });
const SectionsSelectedOrInCartSchema = S.Struct({ anyOf: S.Tuple([SectionsInCartSchema, SectionIsSelectedSchema]) });
const CartViewSchema = makeTriggerEventSchema(TriggerEventName.CartView);
const PreCheckoutPopupSchema = makeTriggerEventSchema(TriggerEventName.PreCheckout);
const MenuSectionRecommendationSchema = S.Struct({
	type: ObjectType,
	required: S.Array(S.String),
	properties: S.Struct({
		triggerEventName: S.Struct({
			type: StringType,
			const: S.Literal(VitrineTriggerEventName.MenuSectionRecommendation)
		}),
		sectionId: S.Struct({
			type: StringType,
			const: S.optionalKey(S.String)
		}),
		timingId: S.optionalKey(TimingIdEnum)
	})
});
function areOrderedEqual(left, right) {
	return left.length === right.length && left.every((value, index) => value === right[index]);
}
/** Templates list `timingId` last in `required`, and only when they carry timings. */
function hasTemplateRequiredKeys(required, ownKeys, timingId) {
	return areOrderedEqual(required, timingId ? [...ownKeys, "timingId"] : ownKeys);
}
function withTimingIds(condition, timingId) {
	return timingId ? {
		...condition,
		timingIds: timingId.enum
	} : condition;
}
function buildObjectSchema(requiredKeys, properties, timingIds) {
	if (timingIds.length === 0) return {
		type: "object",
		required: [...requiredKeys],
		properties
	};
	return {
		type: "object",
		required: [...requiredKeys, "timingId"],
		properties: {
			...properties,
			timingId: {
				type: "string",
				enum: [...timingIds]
			}
		}
	};
}
function buildIdEnumProperties(idField, ids) {
	return { [idField]: {
		type: "string",
		enum: [...ids]
	} };
}
function buildInCartSchema(idField, ids, timingIds) {
	return buildObjectSchema(["cartItems"], { cartItems: {
		type: "array",
		minItems: 1,
		contains: {
			type: "object",
			required: [idField],
			properties: buildIdEnumProperties(idField, ids)
		}
	} }, timingIds);
}
function buildSelectedItemSchema(idField, ids, timingIds) {
	return buildObjectSchema(["selectedItem"], { selectedItem: {
		type: "object",
		required: [idField],
		properties: buildIdEnumProperties(idField, ids)
	} }, timingIds);
}
function buildTriggerEventSchema(triggerEventName, timingIds) {
	return buildObjectSchema(["triggerEventName"], { triggerEventName: {
		type: "string",
		const: triggerEventName
	} }, timingIds);
}
function buildMenuSectionRecommendationSchema(sectionId, timingIds) {
	return buildObjectSchema(["triggerEventName", "sectionId"], {
		triggerEventName: {
			type: "string",
			const: VitrineTriggerEventName.MenuSectionRecommendation
		},
		sectionId: sectionId === void 0 ? { type: "string" } : {
			type: "string",
			const: sectionId
		}
	}, timingIds);
}
const decodeItemsInCart = S.decodeUnknownOption(ItemsInCartSchema, EXACT_PARSE_OPTIONS);
const decodeSectionsInCart = S.decodeUnknownOption(SectionsInCartSchema, EXACT_PARSE_OPTIONS);
const decodeItemIsSelected = S.decodeUnknownOption(ItemIsSelectedSchema, EXACT_PARSE_OPTIONS);
const decodeSectionIsSelected = S.decodeUnknownOption(SectionIsSelectedSchema, EXACT_PARSE_OPTIONS);
const decodeItemsSelectedOrInCart = S.decodeUnknownOption(ItemsSelectedOrInCartSchema, EXACT_PARSE_OPTIONS);
const decodeSectionsSelectedOrInCart = S.decodeUnknownOption(SectionsSelectedOrInCartSchema, EXACT_PARSE_OPTIONS);
const decodeCartView = S.decodeUnknownOption(CartViewSchema, EXACT_PARSE_OPTIONS);
const decodePreCheckoutPopup = S.decodeUnknownOption(PreCheckoutPopupSchema, EXACT_PARSE_OPTIONS);
const decodeMenuSectionRecommendation = S.decodeUnknownOption(MenuSectionRecommendationSchema, EXACT_PARSE_OPTIONS);
function readItemsInCart(schema) {
	const { cartItems, timingId } = schema.properties;
	if (!hasTemplateRequiredKeys(schema.required, ["cartItems"], timingId)) return;
	return withTimingIds({
		name: ConditionSchemaName.OneOfMenuItemsAreInCart,
		itemIds: cartItems.contains.properties.id.enum
	}, timingId);
}
function readSectionsInCart(schema) {
	const { cartItems, timingId } = schema.properties;
	if (!hasTemplateRequiredKeys(schema.required, ["cartItems"], timingId)) return;
	return withTimingIds({
		name: ConditionSchemaName.OneOfMenuSectionsAreInCart,
		sectionIds: cartItems.contains.properties.sectionId.enum
	}, timingId);
}
function readItemIsSelected(schema) {
	const { selectedItem, timingId } = schema.properties;
	if (!hasTemplateRequiredKeys(schema.required, ["selectedItem"], timingId)) return;
	return withTimingIds({
		name: ConditionSchemaName.OneOfMenuItemsIsSelectedItem,
		itemIds: selectedItem.properties.id.enum
	}, timingId);
}
function readSectionIsSelected(schema) {
	const { selectedItem, timingId } = schema.properties;
	if (!hasTemplateRequiredKeys(schema.required, ["selectedItem"], timingId)) return;
	return withTimingIds({
		name: ConditionSchemaName.OneOfMenuSectionsIsSelectedItem,
		sectionIds: selectedItem.properties.sectionId.enum
	}, timingId);
}
/** Both branches of an either-or template carry the same ids and timings. */
function readSelectedOrInCart(name, inCart, selected) {
	if (!inCart || !selected) return;
	return areOrderedEqual(inCart.itemIds ?? [], selected.itemIds ?? []) && areOrderedEqual(inCart.sectionIds ?? [], selected.sectionIds ?? []) && areOrderedEqual(inCart.timingIds ?? [], selected.timingIds ?? []) ? {
		...inCart,
		name
	} : void 0;
}
function readTriggerEvent(name, schema) {
	const { timingId } = schema.properties;
	if (!hasTemplateRequiredKeys(schema.required, ["triggerEventName"], timingId)) return;
	return withTimingIds({ name }, timingId);
}
function readMenuSectionRecommendation(schema) {
	const { sectionId, timingId } = schema.properties;
	if (!hasTemplateRequiredKeys(schema.required, ["triggerEventName", "sectionId"], timingId)) return;
	const name = ConditionSchemaName.MenuSectionIsRecommendation;
	return withTimingIds(sectionId.const === void 0 ? { name } : {
		name,
		sectionId: sectionId.const
	}, timingId);
}
const TEMPLATES = [
	{
		name: ConditionSchemaName.OneOfMenuItemsAreInCart,
		variableNames: ["itemIds", "timingIds"],
		build: ({ itemIds = [], timingIds = [] }) => buildInCartSchema("id", itemIds, timingIds),
		read: (conditionSchema) => Option.flatMapNullishOr(decodeItemsInCart(conditionSchema), readItemsInCart)
	},
	{
		name: ConditionSchemaName.OneOfMenuSectionsAreInCart,
		variableNames: ["sectionIds", "timingIds"],
		build: ({ sectionIds = [], timingIds = [] }) => buildInCartSchema("sectionId", sectionIds, timingIds),
		read: (conditionSchema) => Option.flatMapNullishOr(decodeSectionsInCart(conditionSchema), readSectionsInCart)
	},
	{
		name: ConditionSchemaName.OneOfMenuItemsIsSelectedItem,
		variableNames: ["itemIds", "timingIds"],
		build: ({ itemIds = [], timingIds = [] }) => buildSelectedItemSchema("id", itemIds, timingIds),
		read: (conditionSchema) => Option.flatMapNullishOr(decodeItemIsSelected(conditionSchema), readItemIsSelected)
	},
	{
		name: ConditionSchemaName.OneOfMenuSectionsIsSelectedItem,
		variableNames: ["sectionIds", "timingIds"],
		build: ({ sectionIds = [], timingIds = [] }) => buildSelectedItemSchema("sectionId", sectionIds, timingIds),
		read: (conditionSchema) => Option.flatMapNullishOr(decodeSectionIsSelected(conditionSchema), readSectionIsSelected)
	},
	{
		name: ConditionSchemaName.OneOfMenuItemsAreSelectedItemOrInCart,
		variableNames: ["itemIds", "timingIds"],
		build: ({ itemIds = [], timingIds = [] }) => ({ anyOf: [buildInCartSchema("id", itemIds, timingIds), buildSelectedItemSchema("id", itemIds, timingIds)] }),
		read: (conditionSchema) => Option.flatMapNullishOr(decodeItemsSelectedOrInCart(conditionSchema), ({ anyOf }) => readSelectedOrInCart(ConditionSchemaName.OneOfMenuItemsAreSelectedItemOrInCart, readItemsInCart(anyOf[0]), readItemIsSelected(anyOf[1])))
	},
	{
		name: ConditionSchemaName.OneOfMenuSectionsAreSelectedItemOrInCart,
		variableNames: ["sectionIds", "timingIds"],
		build: ({ sectionIds = [], timingIds = [] }) => ({ anyOf: [buildInCartSchema("sectionId", sectionIds, timingIds), buildSelectedItemSchema("sectionId", sectionIds, timingIds)] }),
		read: (conditionSchema) => Option.flatMapNullishOr(decodeSectionsSelectedOrInCart(conditionSchema), ({ anyOf }) => readSelectedOrInCart(ConditionSchemaName.OneOfMenuSectionsAreSelectedItemOrInCart, readSectionsInCart(anyOf[0]), readSectionIsSelected(anyOf[1])))
	},
	{
		name: ConditionSchemaName.PreCheckoutPopup,
		variableNames: ["timingIds"],
		build: ({ timingIds = [] }) => buildTriggerEventSchema(TriggerEventName.PreCheckout, timingIds),
		read: (conditionSchema) => Option.flatMapNullishOr(decodePreCheckoutPopup(conditionSchema), (schema) => readTriggerEvent(ConditionSchemaName.PreCheckoutPopup, schema))
	},
	{
		name: ConditionSchemaName.CartView,
		variableNames: ["timingIds"],
		build: ({ timingIds = [] }) => buildTriggerEventSchema(TriggerEventName.CartView, timingIds),
		read: (conditionSchema) => Option.flatMapNullishOr(decodeCartView(conditionSchema), (schema) => readTriggerEvent(ConditionSchemaName.CartView, schema))
	},
	{
		name: ConditionSchemaName.MenuSectionIsRecommendation,
		variableNames: ["sectionId", "timingIds"],
		build: ({ sectionId, timingIds = [] }) => buildMenuSectionRecommendationSchema(sectionId, timingIds),
		read: (conditionSchema) => Option.flatMapNullishOr(decodeMenuSectionRecommendation(conditionSchema), readMenuSectionRecommendation)
	}
];
const TEMPLATE_BY_NAME = new Map(TEMPLATES.map((template) => [template.name, template]));
/** The variables a condition's template reads; none for None, Custom, and untemplated names. */
function getRecommendationConditionVariableNames(name) {
	return TEMPLATE_BY_NAME.get(name)?.variableNames ?? [];
}
/**
* The stored JSON Schema for a template condition, built only from the template's own variables.
* `undefined` for None, and for Custom and untemplated names, whose schema the caller supplies.
*/
function buildRecommendationConditionSchema(condition) {
	return TEMPLATE_BY_NAME.get(condition.name)?.build(condition);
}
/** Recovers the template behind a stored condition schema; a schema no template matches is Custom. */
function parseRecommendationCondition(conditionSchema) {
	if (conditionSchema === null || conditionSchema === void 0) return { name: ConditionSchemaName.None };
	for (const template of TEMPLATES) {
		const condition = template.read(conditionSchema);
		if (Option.isSome(condition)) return condition.value;
	}
	return { name: ConditionSchemaName.Custom };
}
//#endregion
export { EDITABLE_CONDITION_SCHEMA_NAMES, RecommendationConditionSchemaJson, buildRecommendationConditionSchema, getRecommendationConditionVariableNames, parseRecommendationCondition };

//# sourceMappingURL=recommendation-condition.js.map